import { apiClient } from '@/lib/api/client';
import { endpoints } from '@/lib/api/endpoints';

import {
  activityListResponseSchema,
  commandSchema,
  deviceListResponseSchema,
  deviceSchema,
  deviceTelemetrySchema,
} from './schemas';
import type {
  ActivityEvent,
  Device,
  DeviceCommand,
  DeviceTelemetry,
  FlowTrendRange,
} from './types';

/**
 * The listing collects ALL of the grower's devices from the backend, then does
 * search / filter / sort / infinite-scroll client-side (story 101847: "all devices
 * collected from backend first, then displayed with infinite scroll"). The seed is
 * small, so a single fetch is faithful and simpler than server paging.
 */
export async function listDevices(): Promise<Device[]> {
  const res = await apiClient.getList(endpoints.devices.list, { params: { limit: 200 } });
  return deviceListResponseSchema.parse(res).data;
}

export async function getDevice(id: string): Promise<Device> {
  return deviceSchema.parse(await apiClient.get(endpoints.devices.detail(id)));
}

export async function getTelemetry(id: string, range: FlowTrendRange): Promise<DeviceTelemetry> {
  return deviceTelemetrySchema.parse(
    await apiClient.get(endpoints.telemetry.latest(id), { params: { range } }),
  );
}

export type IssueCommandBody = {
  type: DeviceCommand['type'];
  /** Free-form payload for stop-timer / schedule drawers (Slice 4). */
  payload?: Record<string, unknown>;
};

/** Issue a remote command; the backend immediately returns the Queued command (101849). */
export async function issueCommand(id: string, body: IssueCommandBody): Promise<DeviceCommand> {
  return commandSchema.parse(await apiClient.post(endpoints.devices.command(id), body));
}

export async function cancelCommand(id: string, commandId: string): Promise<void> {
  await apiClient.delete(`${endpoints.devices.command(id)}/${commandId}`);
}

export async function renameDevice(id: string, name: string): Promise<Device> {
  return deviceSchema.parse(await apiClient.patch(endpoints.devices.detail(id), { name }));
}

export async function getDeviceHistory(id: string): Promise<ActivityEvent[]> {
  const res = await apiClient.getList(endpoints.devices.history(id));
  return activityListResponseSchema.parse(res).data;
}
